/**
 * SampleWorkflowPage — Full regulatory sample workflow per standard
 *
 * Displays the complete, step-by-step wastewater sampling workflow
 * required under each regulatory framework (UK EA, AU EPA, EU UWWTD, US EPA).
 * Each step references the applicable legislation, method standard,
 * frequency requirements, and links to the relevant app section.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useSession } from '@/context/SessionContext';
import {
  ClipboardList, ChevronDown, ChevronRight, ExternalLink,
  Droplets, FlaskConical, FileText, CheckSquare,
  AlertTriangle, Calendar, Database, Shield, Hash,
  BookOpen, TrendingUp, Activity, Beaker, Microscope,
  Archive, Send, Clock, MapPin, Building2, Info,
  CheckCircle2, BarChart3, Layers, RefreshCw,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import type { StandardCode } from '@/types/standards';

/* ─── Types ─────────────────────────────────────────────────────────────────── */
interface WorkflowStep {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  color: string;        // tailwind accent colour
  bgColor: string;
  borderColor: string;
  tasks: Task[];
  regulatoryRef?: string;
  methodStandard?: string;
  timing?: string;
  link?: { label: string; path: string };
}

interface Task {
  label: string;
  detail?: string;
  critical?: boolean;
}

interface StandardWorkflow {
  code: StandardCode;
  flag: string;
  name: string;
  shortName: string;
  country: string;
  accentClass: string;      // tailwind text colour
  borderClass: string;      // tailwind border colour
  bgClass: string;          // tailwind bg colour
  body: string;             // regulatory body name
  legalBase: string;        // primary legislation
  permitType: string;
  certMark: string;
  retention: string;        // record retention period
  reportFreq: string;       // reporting frequency
  samplesPerYear: string;
  complianceRate: string;
  overview: string;
  phases: WorkflowStep[];
  parameters: ParamRow[];
  submissionDeadlines: Deadline[];
}

interface ParamRow {
  param: string;
  unit: string;
  limit: string;
  frequency: string;
  method: string;
  critical?: boolean;
}

interface Deadline {
  event: string;
  timing: string;
  authority: string;
}

/* ─── Workflow data per standard ─────────────────────────────────────────────── */

const UK_PHASES: WorkflowStep[] = [
  {
    id: 'uk_prep', title: 'Pre-Sampling Preparation', subtitle: 'Review permit conditions & prepare equipment',
    icon: ClipboardList, color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/25',
    timing: 'Before each sampling event', regulatoryRef: 'EPR 2016 Schedule 22',
    methodStandard: 'ISO 5667-1 (Water quality — Sampling — Guidance)',
    link: { label: 'Open Site Assessment', path: '/parameters' },
    tasks: [
      { label: 'Review current discharge permit (WDA) conditions and consent schedule', critical: true },
      { label: 'Verify sampling location as defined in permit (typically at final discharge point)', critical: true },
      { label: 'Check MCERTS-certification of sampling equipment and test methods', critical: true },
      { label: 'Confirm laboratory UKAS accreditation for all required determinands' },
      { label: 'Prepare chain-of-custody (CoC) documentation — BS 7755-3.1' },
      { label: 'Calibrate field instruments (pH, DO, conductivity probes) — BS EN ISO 10523' },
      { label: 'Prepare sample containers, preservatives and labelling per EA guidance' },
      { label: 'Arrange composite sampler or flow-proportional sampling if permit requires' },
      { label: 'Record upstream river flows (if discharge to receiving waters)' },
    ],
  },
  {
    id: 'uk_collect', title: 'On-Site Sample Collection', subtitle: 'Influent & effluent grab or composite sampling',
    icon: Droplets, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/25',
    timing: 'Per permit schedule (typically monthly)', regulatoryRef: 'EPR 2016 / EA Monitoring Certification Scheme (MCERTS)',
    methodStandard: 'ISO 5667-10 (Effluent sampling)',
    link: { label: 'Record Influent Sample', path: '/influent-entry' },
    tasks: [
      { label: 'Collect sample at permit-defined location and time of day', critical: true },
      { label: 'Record flow rate at time of sampling (m³/d or L/s) — required for mass load calculation', critical: true },
      { label: 'Record ambient temperature and weather conditions' },
      { label: 'Collect 24-hour composite sample where permit specifies (flow-weighted preferred)' },
      { label: 'Collect minimum 3× grab samples for statistical compliance checks where composite not feasible' },
      { label: 'Measure in-situ parameters: pH (BS EN ISO 10523), temperature, dissolved oxygen (BS EN 25813)' },
      { label: 'Record colour, odour and visible solids (qualitative — regulatory notification trigger)' },
      { label: 'Photograph discharge point and receiving waters for record' },
      { label: 'Complete field data sheet: date, time, operator, weather, flow, in-situ readings' },
      { label: 'Sign chain-of-custody form at point of collection', critical: true },
    ],
  },
  {
    id: 'uk_custody', title: 'Preservation & Chain of Custody', subtitle: 'Correct preservation prevents sample degradation',
    icon: Archive, color: 'text-violet-400', bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/25',
    timing: 'Immediately post-collection', regulatoryRef: 'EA MCERTS Performance Standard M18',
    methodStandard: 'ISO 5667-3 (Guidance on preservation and handling)',
    tasks: [
      { label: 'Preserve BOD samples: refrigerate at 4°C, analyse within 24 hours', critical: true },
      { label: 'Preserve COD samples: acidify to pH < 2 with H₂SO₄, store at 4°C (28 days)', critical: true },
      { label: 'Preserve NH₄-N samples: acidify to pH < 2 with H₂SO₄ or refrigerate (7 days)' },
      { label: 'TSS samples: refrigerate at 4°C, filter within 6 hours' },
      { label: 'Metal samples: acidify to pH < 2 with HNO₃ (28 days)' },
      { label: 'Oil & Grease: refrigerate at 4°C in glass, analyse within 28 days' },
      { label: 'pH: measure in-situ — no preservation possible after collection' },
      { label: 'Label all containers with: site ref, date, time, parameter, preservative, analyst' },
      { label: 'Maintain cold chain during transport (ice or refrigerated courier)', critical: true },
      { label: 'Complete and seal chain-of-custody — each transfer signed and timestamped', critical: true },
    ],
  },
  {
    id: 'uk_lab', title: 'Laboratory Analysis', subtitle: 'UKAS-accredited analysis using approved methods',
    icon: Microscope, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/25',
    timing: 'Within holding times (24 h – 28 d per parameter)',
    regulatoryRef: 'EA MCERTS Performance Standard M18 / BS EN ISO methods',
    methodStandard: 'BS EN 1899-1 (BOD), BS EN ISO 6060 (COD), BS EN 872 (TSS)',
    tasks: [
      { label: 'BOD₅ — BS EN 1899-1 (dilution method) — 5 day incubation at 20°C ± 1°C', critical: true },
      { label: 'COD — BS EN ISO 6060 (dichromate digestion) or equivalent MCERTS method', critical: true },
      { label: 'TSS — BS EN 872 (filtration through 0.45 µm membrane, 105°C drying)' },
      { label: 'Ammonia-N — BS EN ISO 11732 (flow injection / segmented flow analysis)' },
      { label: 'Total Nitrogen — BS EN 12260 (catalytic combustion) or BS EN ISO 11905' },
      { label: 'Total Phosphorus — BS EN ISO 6878 (molybdate colorimetry after digestion)' },
      { label: 'pH — BS EN ISO 10523 (electrometric method)' },
      { label: 'Oil & Grease — gravimetric (solvent extraction) or IR spectroscopy' },
      { label: 'Metals — ICP-MS or ICP-OES (BS EN ISO 17294-2 / BS EN ISO 11885)' },
      { label: 'Include method blanks, duplicates and certified reference materials (CRMs) in each batch' },
      { label: 'Document QC results and uncertainties in laboratory report' },
      { label: 'Issue UKAS-endorsed analytical certificate with measurement uncertainty', critical: true },
    ],
  },
  {
    id: 'uk_data', title: 'Data Entry & Compliance Assessment', subtitle: 'Record results and evaluate against permit limits',
    icon: BarChart3, color: 'text-teal-400', bgColor: 'bg-teal-500/10', borderColor: 'border-teal-500/25',
    timing: 'On receipt of laboratory certificate',
    regulatoryRef: 'EA Compliance Assessment Scheme',
    link: { label: 'Add Influent Sample', path: '/influent-entry' },
    tasks: [
      { label: 'Enter all results into site monitoring register within 48 hours of receipt', critical: true },
      { label: 'Record flow-weighted load (kg/d = concentration mg/L × flow m³/d ÷ 1000)' },
      { label: 'Compare each result against permit condition limits', critical: true },
      { label: 'Flag any exceedances in red — immediate notification trigger', critical: true },
      { label: 'Calculate permit compliance status: pass / fail / near-limit warning (80% threshold)' },
      { label: 'Check 95-percentile compliance for BOD and TSS over rolling 12-month period' },
      { label: 'Update site compliance dashboard (running annual average)' },
      { label: 'Cross-check influent vs effluent to verify treatment efficiency' },
      { label: 'Document any anomalous results with explanation (upset, maintenance, weather)' },
    ],
  },
  {
    id: 'uk_report', title: 'Report Generation', subtitle: 'Produce regulatory-grade sample trend report',
    icon: FileText, color: 'text-rb-brand', bgColor: 'bg-rb-brand/10', borderColor: 'border-rb-brand/25',
    timing: 'Monthly / per sampling event',
    regulatoryRef: 'EPR 2016 Regulation 38 (Record-keeping)',
    link: { label: 'Generate Report', path: '/samples' },
    tasks: [
      { label: 'Generate report with regulatory reference number (RB-UK/YYYY/SMP-XXXXX format)', critical: true },
      { label: 'Include report reference, site permit number, discharge point reference' },
      { label: 'Tabulate all sample results against permit limits with pass/fail status' },
      { label: 'Include trend analysis charts (minimum 12-month rolling window)' },
      { label: 'Include influent vs effluent comparison and treatment efficiency calculation' },
      { label: 'Document sampling method, laboratory accreditation reference and CoC number' },
      { label: 'Include QA/QC summary (blanks, duplicates, CRM recoveries)' },
      { label: 'Sign and date report — authorised signatory required' },
      { label: 'File report in site environmental management system' },
    ],
  },
  {
    id: 'uk_submit', title: 'Regulatory Submission & Notification', subtitle: 'Submit to EA / notify incidents immediately',
    icon: Send, color: 'text-rose-400', bgColor: 'bg-rose-500/10', borderColor: 'border-rose-500/25',
    timing: 'Annual return by 31 March; incidents within 24 hours',
    regulatoryRef: 'EPR 2016 Reg. 38 / Env. Damage Regs 2009',
    tasks: [
      { label: 'Report any permit exceedance to EA within 24 hours by telephone', critical: true },
      { label: 'Submit written exceedance notification within 7 days with corrective actions', critical: true },
      { label: 'Submit annual Environmental Performance Report (EPR) by 31 March each year' },
      { label: 'Submit Pollution Incident data via EA eDMS portal where required' },
      { label: 'Notify EA of any process changes that may affect discharge quality (21 days prior)' },
      { label: 'Respond to EA monitoring requests within timescales specified in permit' },
      { label: 'Provide access for EA site inspections — maintain visitor log' },
    ],
  },
  {
    id: 'uk_records', title: 'Record Keeping & Retention', subtitle: 'Minimum 5 years retention required by law',
    icon: Database, color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-800/40',
    timing: 'Ongoing — minimum 5 years retention (EPR 2016 Reg. 38)',
    regulatoryRef: 'EPR 2016 Schedule 5, Regulation 38',
    link: { label: 'View Records', path: '/influent' },
    tasks: [
      { label: 'Retain all laboratory certificates and chain-of-custody records (5 years minimum)', critical: true },
      { label: 'Retain field data sheets and operator records (5 years minimum)', critical: true },
      { label: 'Retain calibration records for all field instruments' },
      { label: 'Retain permit compliance calculations and trend analyses' },
      { label: 'Maintain site monitoring register accessible to EA on request' },
      { label: 'Archive all regulatory correspondence (permit variations, notices, notifications)' },
      { label: 'Store records securely — must be legible and retrievable within 24 hours' },
    ],
  },
];

const AU_PHASES: WorkflowStep[] = [
  {
    id: 'au_prep', title: 'Pre-Sampling Preparation', subtitle: 'Review EPL conditions & NATA-accredited methods',
    icon: ClipboardList, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/25',
    timing: 'Before each monitoring event', regulatoryRef: 'State EP Acts / EPL Conditions',
    methodStandard: 'APHA Standard Methods for Examination of Water & Wastewater (23rd Ed.)',
    link: { label: 'Open Site Assessment', path: '/parameters' },
    tasks: [
      { label: 'Review Environment Protection Licence (EPL) Schedule 1 monitoring conditions', critical: true },
      { label: 'Confirm sampling points match EPL licence schedule and site plan' },
      { label: 'Verify laboratory holds current NATA accreditation for all required analytes', critical: true },
      { label: 'Prepare EPL Monitoring Report template (state-specific format)' },
      { label: 'Calibrate field instruments per NATA-endorsed procedures' },
      { label: 'Check sample containers for correct type (HDPE, glass, amber) per analyte' },
      { label: 'Prepare preservatives and document batch numbers / expiry dates' },
      { label: 'Review previous sampling results — identify parameters near licence limit' },
    ],
  },
  {
    id: 'au_collect', title: 'On-Site Sample Collection', subtitle: 'EPL-specified points — grab or composite',
    icon: Droplets, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/25',
    timing: 'Per EPL schedule (monthly minimum, weekly for high-risk)', regulatoryRef: 'POEO Act 1997 (NSW) / EP Act 1970 (Vic)',
    methodStandard: 'APHA 1060 B (Sample collection)',
    link: { label: 'Record Effluent Sample', path: '/effluent-entry' },
    tasks: [
      { label: 'Sample at EPL-designated monitoring point (MP) — refer to licence schedule', critical: true },
      { label: 'Record flow rate at time of sampling (kL/day or ML/year for licence reporting)', critical: true },
      { label: 'Collect composite sample (time-proportional or flow-proportional as specified in EPL)' },
      { label: 'Measure in-situ pH, EC, temperature, DO using calibrated equipment' },
      { label: 'Record ambient temperature, wind, rainfall — note any unusual conditions' },
      { label: 'Collect separate samples for metals (unfiltered unless EPL states filtered)' },
      { label: 'Photograph monitoring point, receiving water, and any visible discharge impacts' },
      { label: 'Complete EPL monitoring field sheet — all details including weather code' },
      { label: 'Sign field data sheet — date, time, operator name and company', critical: true },
    ],
  },
  {
    id: 'au_custody', title: 'Preservation & Chain of Custody', subtitle: 'NATA-required handling and preservation',
    icon: Archive, color: 'text-violet-400', bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/25',
    timing: 'Immediately post-collection', regulatoryRef: 'NATA Technical Note 17',
    methodStandard: 'APHA 1060 C (Preservation)',
    tasks: [
      { label: 'BOD: refrigerate at 1–4°C immediately, analyse within 24 hours', critical: true },
      { label: 'COD: acidify with H₂SO₄ to pH < 2, refrigerate — 28 days holding time' },
      { label: 'TSS: refrigerate at 4°C, no chemical preservation — analyse within 7 days' },
      { label: 'Nutrients (TN, TP, NH₃): acidify to pH < 2 or freeze — 28 days' },
      { label: 'Metals: acidify to pH < 2 with HNO₃ (ultrapure), refrigerate — 6 months' },
      { label: 'Oil & Grease: glass container, pH < 2 with HCl, refrigerate — 28 days' },
      { label: 'Bacteriological: sterile container, sodium thiosulfate, refrigerate, 6 hours max' },
      { label: 'Use approved NATA CoC form — unique sample ID, all transfers documented', critical: true },
      { label: 'Cold chain integrity maintained — temperature monitored in transit', critical: true },
    ],
  },
  {
    id: 'au_lab', title: 'NATA-Accredited Laboratory Analysis', subtitle: 'All analyses by NATA-accredited laboratory',
    icon: Microscope, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/25',
    timing: 'Within APHA method holding times',
    regulatoryRef: 'POEO Act 1997 s.91, EP Act 1970 s.31A — accredited analysis required',
    methodStandard: 'APHA 23rd Ed. / AS/NZS 5667 series',
    tasks: [
      { label: 'BOD₅ — APHA 5210B (Winkler) or 5210D (respirometric) — 20°C ± 1°C', critical: true },
      { label: 'COD — APHA 5220C (closed reflux colorimetry) or APHA 5220D (titrimetric)' },
      { label: 'TSS — APHA 2540D (filtration 1.2 µm glass fibre, dried at 103–105°C)' },
      { label: 'Ammonia-N — APHA 4500-NH₃ (distillation-titration or ISE)' },
      { label: 'TN — APHA 4500-N (persulfate digestion + Nesslerisation or colorimetry)' },
      { label: 'TP — APHA 4500-P (acid digestion + molybdate colorimetry)' },
      { label: 'Oil & Grease — APHA 5520B (partition-gravimetric) or 5520F (partition-IR)' },
      { label: 'Metals — AS/NZS 2399 (ICP-MS or ICP-OES) — include total and dissolved fractions' },
      { label: 'pH — APHA 4500-H⁺ B (electrometric) — calibrate with 2-point buffer' },
      { label: 'EC — APHA 2510B (electrometric) — temperature-corrected to 25°C' },
      { label: 'Report as validated by NATA-authorised signatory with NATA endorsement', critical: true },
      { label: 'Include QC: LCS, MS/MSD, duplicates, method blank — document in NATA report' },
    ],
  },
  {
    id: 'au_data', title: 'Data Entry & EPL Compliance Assessment', subtitle: 'Compare results against licence conditions',
    icon: BarChart3, color: 'text-teal-400', bgColor: 'bg-teal-500/10', borderColor: 'border-teal-500/25',
    timing: 'Within 7 days of laboratory results',
    regulatoryRef: 'EPL Licence Conditions / POEO Act s.91',
    link: { label: 'Compliance Gateway', path: '/compliance-gateway' },
    tasks: [
      { label: 'Enter all results against EPL monitoring conditions (concentrations AND loads)', critical: true },
      { label: 'Calculate mass load: concentration (mg/L) × flow (kL/day) ÷ 1000 = kg/day' },
      { label: 'Compare results to EPL Table 1 (concentration limits) and Table 2 (mass limits)', critical: true },
      { label: 'Flag exceedances — trigger mandatory pollution incident reporting', critical: true },
      { label: 'Calculate licence compliance rate for annual EPL return (target 100%)' },
      { label: 'Update site monitoring register with flow-weighted average concentrations' },
      { label: 'Compare influent vs effluent removal efficiency (% reduction per parameter)' },
      { label: 'Document any abnormal operational conditions that affected results' },
    ],
  },
  {
    id: 'au_report', title: 'Report Generation', subtitle: 'Prepare EPL compliance monitoring report',
    icon: FileText, color: 'text-rb-brand', bgColor: 'bg-rb-brand/10', borderColor: 'border-rb-brand/25',
    timing: 'Monthly for EPL records; annual for submission',
    regulatoryRef: 'POEO Act 1997 / Protection of Environment Operations (Monitoring) Policy',
    link: { label: 'Generate Report', path: '/samples' },
    tasks: [
      { label: 'Generate report with regulatory reference (RB-AU/YYYY/SMP-XXXXX format)', critical: true },
      { label: 'Include EPL licence number, monitoring point references, monitoring period' },
      { label: 'Tabulate all results vs EPL limits — include units, holding times, exceedances' },
      { label: 'Include NATA laboratory certificate reference numbers and accreditation number' },
      { label: 'Include flow data: daily average, peak, total volume for monitoring period' },
      { label: 'Include trend charts — 12 months rolling for all EPL parameters' },
      { label: 'Include QA/QC summary and method references' },
      { label: 'Prepare Annual EPL Return in EPA reporting format', critical: true },
    ],
  },
  {
    id: 'au_submit', title: 'EPA Submission & Pollution Incident Reporting', subtitle: 'Mandatory reporting obligations',
    icon: Send, color: 'text-rose-400', bgColor: 'bg-rose-500/10', borderColor: 'border-rose-500/25',
    timing: 'Annual return by 30 September; pollution incidents immediately',
    regulatoryRef: 'POEO Act 1997 s.148 / EP Act 1970 s.59BJ',
    tasks: [
      { label: 'Notify EPA of pollution incident by phone IMMEDIATELY — before controlling', critical: true },
      { label: 'Submit written pollution incident notification within 7 days', critical: true },
      { label: 'Submit Annual EPL return via EPA portal by 30 September each year', critical: true },
      { label: 'Include all monitoring data, exceedances and corrective actions in annual return' },
      { label: 'Pay annual EPL administration fee at time of return' },
      { label: 'Publish monitoring data on company website where EPL requires (NSW)' },
      { label: 'Respond to EPA notice requests within specified timeframe (typically 21 days)' },
    ],
  },
  {
    id: 'au_records', title: 'Record Keeping & Retention', subtitle: 'Minimum 4–5 years per state requirements',
    icon: Database, color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-800/40',
    timing: 'Ongoing — 4 years (NSW/QLD), 5 years (VIC/SA/WA)',
    regulatoryRef: 'POEO Regulation 2014 cl.100 (NSW) / Environment Protection Regs 2017 (VIC)',
    link: { label: 'View Records', path: '/effluent' },
    tasks: [
      { label: 'Retain all NATA laboratory reports with NATA endorsement (5 years)', critical: true },
      { label: 'Retain all field data sheets, photographs and CoC documents (5 years)', critical: true },
      { label: 'Retain EPL annual returns and EPA correspondence' },
      { label: 'Retain calibration records for all monitoring equipment' },
      { label: 'Retain operator training records and competency certificates' },
      { label: 'Make records available to EPA inspectors within 24 hours of request' },
      { label: 'Retain pollution incident reports, investigations and corrective action records' },
    ],
  },
];

const EU_PHASES: WorkflowStep[] = [
  {
    id: 'eu_prep', title: 'Pre-Sampling Preparation', subtitle: 'Review permit & Water Framework Directive requirements',
    icon: ClipboardList, color: 'text-violet-400', bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/25',
    timing: 'Before each sampling event', regulatoryRef: 'EU UWWTD 2024/3019 Article 18 / IED 2010/75/EU',
    methodStandard: 'EN ISO 5667 (Water sampling)',
    link: { label: 'Open Site Assessment', path: '/parameters' },
    tasks: [
      { label: 'Review operational permit (Betriebsgenehmigung / Autorisation de rejet) conditions', critical: true },
      { label: 'Confirm treatment plant meets population equivalent (PE) threshold classification' },
      { label: 'Verify sampling complies with UWWTD 2024 Annex I Table 1 determinands', critical: true },
      { label: 'Check accredited laboratory certification (e.g. DAkkS, COFRAC, ENAC, ACCREDIA)' },
      { label: 'Prepare sample programme per EN ISO 5667-1 (programme design)' },
      { label: 'Calibrate instruments per EN ISO standards' },
      { label: 'Prepare composite sampling equipment for 24-hour flow-proportional collection' },
      { label: 'Coordinate with competent authority sampling inspector where required' },
    ],
  },
  {
    id: 'eu_collect', title: 'On-Site Sample Collection', subtitle: '24-hour composite sampling — UWWTD requirement',
    icon: Droplets, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/25',
    timing: '12 samples per year minimum (>10,000 PE); 24 samples (>100,000 PE)',
    regulatoryRef: 'UWWTD 2024/3019 Annex I Table 1 / EN ISO 5667-10',
    link: { label: 'Record Effluent Sample', path: '/effluent-entry' },
    tasks: [
      { label: 'Collect 24-hour flow-proportional composite sample — UWWTD mandatory', critical: true },
      { label: 'Record daily and peak flow rate (Q m³/day) — required for PE calculation' },
      { label: 'Sample at final effluent point after all treatment (not intermediate)' },
      { label: 'Minimum 12 samples/year for plants 2,000–100,000 PE distributed across all seasons' },
      { label: 'Minimum 24 samples/year for plants >100,000 PE' },
      { label: 'Measure in-situ pH, temperature, EC using calibrated probes' },
      { label: 'Collect sludge samples if biosolids are applied to agricultural land (separate schedule)' },
      { label: 'Document in field register: date, time, flow, operator, equipment ID' },
    ],
  },
  {
    id: 'eu_custody', title: 'Preservation & Handling', subtitle: 'EN ISO 5667-3 handling requirements',
    icon: Archive, color: 'text-pink-400', bgColor: 'bg-pink-500/10', borderColor: 'border-pink-500/25',
    timing: 'Immediately post-collection', regulatoryRef: 'EN ISO 5667-3:2018',
    tasks: [
      { label: 'BOD₅: refrigerate at 1–4°C — analyse within 24 hours', critical: true },
      { label: 'COD: add H₂SO₄ to pH < 2 — 28 days holding time at 4°C' },
      { label: 'TSS: refrigerate at 4°C — filter within 6 hours, no chemical preservation' },
      { label: 'TN/TP: acidify to pH < 2, refrigerate or freeze — 28 days' },
      { label: 'NH₄-N: acidify to pH < 2 or freeze — 7 days refrigerated' },
      { label: 'All containers pre-labelled in accordance with accreditation body requirements' },
      { label: 'Maintain documented chain of custody throughout transport' },
    ],
  },
  {
    id: 'eu_lab', title: 'Accredited Laboratory Analysis', subtitle: 'EN ISO methods — national accreditation required',
    icon: Microscope, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/25',
    timing: 'Within EN ISO holding times',
    regulatoryRef: 'UWWTD 2024 Annex I — reference methods',
    tasks: [
      { label: 'BOD₅ — EN 1899-1:1998 (dilution method) or EN 1899-2 (miniaturised)', critical: true },
      { label: 'COD — EN ISO 6060:1989 (dichromate reflux) or equivalent', critical: true },
      { label: 'TSS — EN 872:2005 (filtration method)', critical: true },
      { label: 'Total Nitrogen — EN 12260:2003 (bound nitrogen — catalytic combustion)' },
      { label: 'Total Phosphorus — EN ISO 6878:2004 (molybdate colorimetry after digestion)' },
      { label: 'pH — EN ISO 10523:2012 (electrometric)' },
      { label: 'Metals — EN ISO 17294-2 (ICP-MS) — mandatory for List I/II substances (76/464/EEC)' },
      { label: 'Include QC: certified reference materials, method blanks, duplicate analysis' },
      { label: 'Issue accreditation-endorsed report — include accreditation body mark', critical: true },
    ],
  },
  {
    id: 'eu_data', title: 'Data Entry & WFD Compliance Assessment', subtitle: 'Assess 95-percentile compliance per UWWTD',
    icon: BarChart3, color: 'text-teal-400', bgColor: 'bg-teal-500/10', borderColor: 'border-teal-500/25',
    timing: 'On receipt of accredited results',
    regulatoryRef: 'UWWTD 2024 Annex I Table 1 — 95-percentile compliance',
    link: { label: 'Compliance Gateway', path: '/compliance-gateway' },
    tasks: [
      { label: '95% of samples must comply with concentration OR reduction limits — not both required', critical: true },
      { label: 'Record as pass if concentration ≤ limit OR removal efficiency ≥ minimum (%)', critical: true },
      { label: 'BOD₅: ≤ 25 mg/L or ≥ 70–90% removal; COD: ≤ 125 mg/L or ≥ 75% removal' },
      { label: 'TSS: ≤ 35 mg/L or ≥ 90% removal (>100,000 PE)' },
      { label: 'TN: ≤ 10 mg/L (>100,000 PE) or ≤ 15 mg/L (10,000–100,000 PE)' },
      { label: 'TP: ≤ 1 mg/L (>100,000 PE) or ≤ 2 mg/L (10,000–100,000 PE)' },
      { label: 'Calculate annual compliance rate for competent authority reporting' },
      { label: 'Update national monitoring database (WISE reporting — urban wastewater)' },
    ],
  },
  {
    id: 'eu_report', title: 'Report & National Database Submission', subtitle: 'UWWTD reporting to competent authority',
    icon: FileText, color: 'text-rb-brand', bgColor: 'bg-rb-brand/10', borderColor: 'border-rb-brand/25',
    timing: 'Annual report to competent authority', regulatoryRef: 'UWWTD 2024 Article 21',
    link: { label: 'Generate Report', path: '/samples' },
    tasks: [
      { label: 'Generate report with regulatory reference (RB-EU/YYYY/SMP-XXXXX format)', critical: true },
      { label: 'Include UWWTD treatment plant identifier (agglomeration code)' },
      { label: 'Report all 12/24 sample results with dates, flows and compliance status' },
      { label: 'Calculate annual 95-percentile compliance statistics' },
      { label: 'Submit to national competent authority by deadline (typically Q1 following year)' },
      { label: 'Data forwarded to European Environment Agency (EEA) WISE database' },
      { label: 'Publish data on public register where member state law requires' },
    ],
  },
  {
    id: 'eu_records', title: 'Record Keeping', subtitle: 'Minimum 5 years per UWWTD 2024',
    icon: Database, color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-800/40',
    timing: 'Ongoing — 5 years minimum',
    regulatoryRef: 'UWWTD 2024 Article 21 / IED 2010/75/EU Article 72',
    tasks: [
      { label: 'Retain all accredited laboratory reports (5 years minimum)', critical: true },
      { label: 'Retain competent authority annual returns and correspondence', critical: true },
      { label: 'Maintain treatment plant operational log — daily flow, treatment performance' },
      { label: 'Retain calibration records, operator qualifications, maintenance logs' },
      { label: 'Make records available to competent authority on request' },
    ],
  },
];

const US_PHASES: WorkflowStep[] = [
  {
    id: 'us_prep', title: 'Pre-Sampling Preparation', subtitle: 'Review NPDES permit & QAPP requirements',
    icon: ClipboardList, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/25',
    timing: 'Before each sampling event',
    regulatoryRef: 'CWA §402 / 40 CFR Part 122 / NPDES Permit',
    methodStandard: '40 CFR Part 136 — Guidelines Establishing Test Procedures',
    link: { label: 'Open Site Assessment', path: '/parameters' },
    tasks: [
      { label: 'Review NPDES permit effluent limitations (Table B/C) and monitoring schedule', critical: true },
      { label: 'Confirm sampling location matches permit-defined outfall (001, 002, etc.)', critical: true },
      { label: 'Verify Quality Assurance Project Plan (QAPP) is current and approved by state agency', critical: true },
      { label: 'Confirm certified laboratory for all required analytes per 40 CFR Part 136', critical: true },
      { label: 'Prepare DMR (Discharge Monitoring Report) form for current reporting period' },
      { label: 'Calibrate field instruments per EPA QA/QC requirements (QA-6, QA-8)' },
      { label: 'Check sampler equipment — composite sampler programming for permit-required frequency' },
      { label: 'Review any state-specific requirements (state water quality standards may be more stringent)' },
    ],
  },
  {
    id: 'us_collect', title: 'On-Site Sample Collection', subtitle: 'Grab or composite per permit specification',
    icon: Droplets, color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/25',
    timing: 'Per permit schedule — monthly DMR, weekly/daily for specific parameters',
    regulatoryRef: '40 CFR §122.41(j) / 40 CFR Part 136',
    link: { label: 'Record Influent Sample', path: '/influent-entry' },
    tasks: [
      { label: 'Sample at NPDES-designated outfall location — permit specifies grab vs composite', critical: true },
      { label: 'Record flow rate in MGD (million gallons per day) as required for DMR', critical: true },
      { label: 'Composite sample: 24-hour flow-proportional (preferred) or time-proportional' },
      { label: 'Grab sample: instantaneous — only where permit specifically allows for that parameter' },
      { label: 'pH, temperature, DO: grab samples only — cannot composite these parameters' },
      { label: 'Record in field log: date, time (24h format), sampler ID, flow, weather, unusual conditions' },
      { label: 'Collect QC samples: field blank, equipment blank, matrix spike duplicate (MS/MSD)' },
      { label: 'Complete chain-of-custody form per state agency requirements', critical: true },
    ],
  },
  {
    id: 'us_custody', title: 'Preservation & Chain of Custody', subtitle: '40 CFR Part 136 Table II preservation requirements',
    icon: Archive, color: 'text-orange-400', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/25',
    timing: 'Immediately post-collection — strict 40 CFR holding times',
    regulatoryRef: '40 CFR Part 136 Table II — Holding Times and Preservation',
    tasks: [
      { label: 'CBOD₅/BOD₅: 4°C cool immediately, analyse within 48 hours', critical: true },
      { label: 'COD: H₂SO₄ to pH < 2, 4°C — 28 days holding time', critical: true },
      { label: 'TSS: 4°C cool, no preservation — 7 days holding time' },
      { label: 'Ammonia-N: H₂SO₄ to pH < 2 or 4°C — 28 days (acid) or 7 days (cold)' },
      { label: 'Total Nitrogen (TKN): H₂SO₄ to pH < 2, 4°C — 28 days' },
      { label: 'Total Phosphorus: H₂SO₄ to pH < 2, 4°C — 28 days' },
      { label: 'Metals: HNO₃ to pH < 2, 4°C — 6 months' },
      { label: 'Oil & Grease: HCl to pH < 2, 4°C, glass — 28 days' },
      { label: 'Chain-of-custody: seal containers, sign and date at collection — every transfer documented', critical: true },
    ],
  },
  {
    id: 'us_lab', title: 'Certified Laboratory Analysis', subtitle: 'EPA-approved methods — 40 CFR Part 136',
    icon: Microscope, color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/25',
    timing: 'Within 40 CFR Part 136 Table II holding times',
    regulatoryRef: '40 CFR Part 136 — Approved Test Procedures',
    tasks: [
      { label: 'CBOD₅ — EPA Method 405.1 or SM 5210B — 5-day incubation 20°C ± 1°C', critical: true },
      { label: 'COD — EPA Method 410.4 or SM 5220C (closed reflux colorimetric)', critical: true },
      { label: 'TSS — EPA Method 160.2 or SM 2540D (filtration 103–105°C)' },
      { label: 'Ammonia-N — EPA Method 350.1/350.2 or SM 4500-NH₃' },
      { label: 'TKN — EPA Method 351.2 or SM 4500-N' },
      { label: 'Total Phosphorus — EPA Method 365.1 or SM 4500-P' },
      { label: 'Oil & Grease — EPA Method 1664A (hexane extraction gravimetric)' },
      { label: 'Metals — EPA Method 200.7 (ICP-OES) or 200.8 (ICP-MS)' },
      { label: 'pH — EPA Method 150.1 or SM 4500-H⁺ B (electrometric, in-situ preferred)' },
      { label: 'Must use only 40 CFR Part 136 approved methods — no substitutions without variance', critical: true },
      { label: 'Issue analytical report signed by laboratory QA/QC officer', critical: true },
      { label: 'Include all QC: MB, MS, MSD, LCS, LCSD, duplicates, RPDs' },
    ],
  },
  {
    id: 'us_data', title: 'Data Review & NPDES Compliance', subtitle: 'Compare to permit effluent limitations',
    icon: BarChart3, color: 'text-teal-400', bgColor: 'bg-teal-500/10', borderColor: 'border-teal-500/25',
    timing: 'Before DMR submission deadline',
    regulatoryRef: 'CWA §309 — Enforcement / 40 CFR §122.41',
    link: { label: 'Compliance Gateway', path: '/compliance-gateway' },
    tasks: [
      { label: 'Compare results to NPDES permit Table B (monthly average) and Table C (daily maximum)', critical: true },
      { label: 'Calculate monthly average from all grab/composite samples in reporting period', critical: true },
      { label: 'Flag any exceedance — CWA §309 violation — immediate notification required', critical: true },
      { label: 'Calculate mass-based loads (lbs/day): concentration (mg/L) × flow (MGD) × 8.34' },
      { label: 'Verify minimum sampling frequency has been met (permit requirement)' },
      { label: 'Document any missing samples — reasons required on DMR' },
      { label: 'Review bioassay / whole effluent toxicity (WET) results if required by permit' },
    ],
  },
  {
    id: 'us_report', title: 'DMR Submission & Report Generation', subtitle: 'Monthly Discharge Monitoring Report to NPDES authority',
    icon: FileText, color: 'text-rb-brand', bgColor: 'bg-rb-brand/10', borderColor: 'border-rb-brand/25',
    timing: 'DMR due 28th day of month following monitoring month',
    regulatoryRef: 'CWA §402(l) / 40 CFR §122.41(l)(4) — DMR due dates',
    link: { label: 'Generate Report', path: '/samples' },
    tasks: [
      { label: 'Generate regulatory report (RB-US/NPDES/YYYY/SMP-XXXXX format)', critical: true },
      { label: 'Complete EPA Form 2F or state-equivalent DMR — all permit parameters', critical: true },
      { label: 'Enter monitoring data into NeT (NPDES Electronic Reporting Tool) or state portal', critical: true },
      { label: 'Calculate monthly average and maximum day values for each parameter' },
      { label: 'Include flow data: average daily, maximum daily, minimum daily (in MGD)' },
      { label: 'Complete "No Discharge" entries for any outfalls not active during period' },
      { label: 'Sign DMR — certifier must be Principal Executive Officer or delegated signatory', critical: true },
      { label: 'Retain copy of submitted DMR in site records' },
    ],
  },
  {
    id: 'us_notify', title: 'Permit Exceedance Notification', subtitle: 'CWA reporting obligations — strict deadlines',
    icon: Send, color: 'text-rose-400', bgColor: 'bg-rose-500/10', borderColor: 'border-rose-500/25',
    timing: '24 hours verbal; 5 days written for significant violations',
    regulatoryRef: 'CWA §309 / 40 CFR §122.41(l)(6) — Notification of Non-Compliance',
    tasks: [
      { label: 'Notify permitting authority within 24 hours of discovery of significant violation', critical: true },
      { label: 'Submit written notification within 5 days — describe cause and corrective action', critical: true },
      { label: 'Report spills/bypasses to State Emergency Response Commission (SERC) if EPCRA threshold exceeded' },
      { label: 'Notify local emergency planning committee for certain toxic chemical releases' },
      { label: 'Document all notifications with date, time, recipient name and ID number' },
    ],
  },
  {
    id: 'us_records', title: 'Record Keeping', subtitle: '3 years minimum — DMR records for 5 years',
    icon: Database, color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-800/40',
    timing: 'Minimum 3 years (general); 5 years (DMR and permit)',
    regulatoryRef: '40 CFR §122.41(j)(2) — Record Retention',
    link: { label: 'View Records', path: '/influent' },
    tasks: [
      { label: 'Retain all monitoring data, laboratory reports and QA/QC records (3 years minimum)', critical: true },
      { label: 'Retain all DMRs submitted — accessible within 72 hours of request', critical: true },
      { label: 'Retain NPDES permit, all permit modifications and correspondence (active + 3 years)' },
      { label: 'Retain chain-of-custody forms and field data sheets' },
      { label: 'Retain QAPP and any revisions — must be available to regulator' },
      { label: 'Make all records available to EPA and state agency inspectors on request' },
    ],
  },
];

const US_PARAMETERS: ParamRow[] = [
  { param: 'CBOD₅', unit: 'mg/L', limit: '30 avg / 45 max', frequency: 'Monthly (2 grabs min.)', method: 'EPA 405.1', critical: true },
  { param: 'TSS', unit: 'mg/L', limit: '30 avg / 45 max', frequency: 'Monthly (2 grabs min.)', method: 'EPA 160.2', critical: true },
  { param: 'pH', unit: 'S.U.', limit: '6.0 – 9.0 S.U.', frequency: 'Weekly (grab)', method: 'EPA 150.1' },
  { param: 'Flow', unit: 'MGD', limit: 'Permit-specific', frequency: 'Continuous or daily', method: 'Calibrated meter', critical: true },
  { param: 'Total N (TKN)', unit: 'mg/L', limit: 'Permit-specific', frequency: 'Monthly', method: 'EPA 351.2' },
  { param: 'Total Phosphorus', unit: 'mg/L', limit: 'Permit-specific', frequency: 'Monthly', method: 'EPA 365.1' },
  { param: 'Oil & Grease', unit: 'mg/L', limit: '15 avg / 20 max (typical)', frequency: 'Monthly', method: 'EPA 1664A' },
  { param: 'Ammonia-N', unit: 'mg/L', limit: 'Permit-specific (seasonal)', frequency: 'Monthly–Weekly', method: 'EPA 350.1' },
  { param: 'Total Metals', unit: 'µg/L', limit: 'Permit-specific', frequency: 'Quarterly', method: 'EPA 200.7/200.8' },
  { param: 'WET (acute)', unit: 'TUa', limit: '< 1.0 TUa', frequency: 'Quarterly', method: 'EPA 821-R-02-012' },
];

const UK_PARAMETERS: ParamRow[] = [
  { param: 'BOD₅', unit: 'mg/L', limit: '≤ 25', frequency: 'Monthly composite', method: 'BS EN 1899-1', critical: true },
  { param: 'COD', unit: 'mg/L', limit: '≤ 125', frequency: 'Monthly composite', method: 'BS EN ISO 6060', critical: true },
  { param: 'TSS', unit: 'mg/L', limit: '≤ 35', frequency: 'Monthly composite', method: 'BS EN 872' },
  { param: 'pH', unit: 'pH unit', limit: '6.0 – 9.0', frequency: 'Weekly grab', method: 'BS EN ISO 10523' },
  { param: 'Ammonia-N', unit: 'mg/L', limit: '≤ 5.0', frequency: 'Monthly', method: 'BS EN ISO 11732' },
  { param: 'Total Phosphorus', unit: 'mg/L', limit: '≤ 1.0 (sensitive waters)', frequency: 'Monthly', method: 'BS EN ISO 6878' },
  { param: 'Total Nitrogen', unit: 'mg/L', limit: '≤ 10 (>100k PE)', frequency: 'Monthly', method: 'BS EN 12260' },
  { param: 'Oil & Grease', unit: 'mg/L', limit: '≤ 15', frequency: 'Monthly', method: 'Gravimetric / IR' },
  { param: 'Temperature', unit: '°C', limit: '≤ 30', frequency: 'Continuous / grab', method: 'BS EN ISO 10523' },
  { param: 'Lead', unit: 'µg/L', limit: '≤ 500', frequency: 'Quarterly', method: 'BS EN ISO 11885 (ICP)' },
  { param: 'Chromium', unit: 'µg/L', limit: '≤ 1000', frequency: 'Quarterly', method: 'BS EN ISO 11885' },
];

const AU_PARAMETERS: ParamRow[] = [
  { param: 'BOD₅', unit: 'mg/L', limit: '≤ 300 (trade waste typical)', frequency: 'Monthly composite', method: 'APHA 5210B', critical: true },
  { param: 'COD', unit: 'mg/L', limit: '≤ 500', frequency: 'Monthly composite', method: 'APHA 5220C', critical: true },
  { param: 'TSS', unit: 'mg/L', limit: '≤ 60', frequency: 'Monthly composite', method: 'APHA 2540D' },
  { param: 'pH', unit: 'pH unit', limit: '6.0 – 10.0', frequency: 'Continuous / monthly', method: 'APHA 4500-H⁺ B' },
  { param: 'Ammonia-N', unit: 'mg/L', limit: '≤ 100 (unionised <5)', frequency: 'Monthly', method: 'APHA 4500-NH₃' },
  { param: 'Total Nitrogen', unit: 'mg/L', limit: '≤ 200', frequency: 'Monthly', method: 'APHA 4500-N' },
  { param: 'Total Phosphorus', unit: 'mg/L', limit: '≤ 10', frequency: 'Monthly', method: 'APHA 4500-P' },
  { param: 'Oil & Grease', unit: 'mg/L', limit: '≤ 15 (max 30)', frequency: 'Monthly', method: 'APHA 5520B/F' },
  { param: 'EC', unit: 'µS/cm', limit: '≤ 2500', frequency: 'Monthly', method: 'APHA 2510B' },
  { param: 'Lead', unit: 'mg/L', limit: '≤ 0.05', frequency: 'Quarterly', method: 'AS/NZS 2399 (ICP)' },
  { param: 'Temperature', unit: '°C', limit: '≤ 40', frequency: 'Continuous', method: 'APHA 2550B' },
];

const EU_PARAMETERS: ParamRow[] = [
  { param: 'BOD₅', unit: 'mg/L O₂', limit: '≤ 25 or ≥ 70–90% removal', frequency: '12/year (≥10k PE); 24/year (≥100k PE)', method: 'EN 1899-1', critical: true },
  { param: 'COD', unit: 'mg/L O₂', limit: '≤ 125 or ≥ 75% removal', frequency: 'Same as BOD', method: 'EN ISO 6060', critical: true },
  { param: 'TSS', unit: 'mg/L', limit: '≤ 35 or ≥ 90% removal', frequency: 'Same as BOD', method: 'EN 872', critical: true },
  { param: 'Total Nitrogen', unit: 'mg/L N', limit: '≤ 10 (>100k PE) / ≤ 15 (10–100k PE)', frequency: '12/year min.', method: 'EN 12260' },
  { param: 'Total Phosphorus', unit: 'mg/L P', limit: '≤ 1 (>100k PE) / ≤ 2 (10–100k PE)', frequency: '12/year min.', method: 'EN ISO 6878' },
  { param: 'pH', unit: 'pH unit', limit: '6.0 – 9.0', frequency: 'Continuous', method: 'EN ISO 10523' },
  { param: 'Ammonia', unit: 'mg/L N', limit: 'Member state specific', frequency: 'Monthly', method: 'EN ISO 11732' },
];

/* ─── Standards config ───────────────────────────────────────────────────────── */
const STANDARDS: StandardWorkflow[] = [
  {
    code: 'UK_EA',
    flag: '🇬🇧', name: 'UK Environment Agency', shortName: 'UK EA', country: 'United Kingdom',
    accentClass: 'text-blue-400', borderClass: 'border-blue-500/30', bgClass: 'bg-blue-500/10',
    body: 'Environment Agency (England) / Natural Resources Wales / SEPA (Scotland)',
    legalBase: 'Environmental Permitting (England and Wales) Regulations 2016 (SI 2016/1154) / Urban Wastewater Treatment (England and Wales) Regulations 2024',
    permitType: 'Water Discharge Activity Permit / Trade Effluent Consent',
    certMark: 'UKAS / MCERTS',
    retention: '5 years (EPR 2016 Regulation 38)',
    reportFreq: 'Monthly monitoring; Annual Environmental Performance Report',
    samplesPerYear: '12+ (monthly); some sites weekly',
    complianceRate: '95-percentile compliance required for BOD / TSS / COD',
    overview: 'UK Environment Agency-regulated discharges require an Environmental Permit (water discharge activity) or Trade Effluent Consent. Monitoring must use MCERTS-certified methods and UKAS-accredited laboratories. Results are reported monthly in site records and annually via the Environment Agency Electronic Document Management System (eDMS). Permit exceedances trigger mandatory notification within 24 hours.',
    phases: UK_PHASES,
    parameters: UK_PARAMETERS,
    submissionDeadlines: [
      { event: 'Permit exceedance notification (verbal)', timing: 'Within 24 hours of discovery', authority: 'Environment Agency / NRW / SEPA' },
      { event: 'Permit exceedance written report', timing: 'Within 7 days', authority: 'Environment Agency' },
      { event: 'Annual Environmental Performance Report', timing: '31 March each year', authority: 'Environment Agency eDMS' },
      { event: 'Process change notification', timing: '21 days before change', authority: 'Environment Agency' },
      { event: 'Permit variation application', timing: 'As required — allow 4 months processing', authority: 'Environment Agency' },
    ],
  },
  {
    code: 'AU_EPA',
    flag: '🇦🇺', name: 'Australian EPA', shortName: 'AU EPA', country: 'Australia / New Zealand',
    accentClass: 'text-emerald-400', borderClass: 'border-emerald-500/30', bgClass: 'bg-emerald-500/10',
    body: 'NSW EPA / VIC EPA / QLD DES / SA EPA / WA DWER / NT EPA / TAS EPA',
    legalBase: 'POEO Act 1997 (NSW) / EP Act 1970 (VIC) / EP Act 1994 (QLD) / EP Act 1993 (SA) / EP Act 1986 (WA)',
    permitType: 'Environment Protection Licence (EPL) / Works Approval',
    certMark: 'NATA (National Association of Testing Authorities)',
    retention: '4 years (NSW/QLD) — 5 years (VIC/SA/WA)',
    reportFreq: 'Monthly EPL compliance records; Annual EPL Return',
    samplesPerYear: '12 minimum (monthly); High-risk sites: 52 (weekly)',
    complianceRate: '100% EPL compliance target (any exceedance = reportable incident)',
    overview: 'Australian EPA-licenced discharges operate under state-specific Environment Protection Licences. All analysis must be conducted by NATA-accredited laboratories using APHA Standard Methods. Monthly monitoring data is maintained on-site; an Annual EPL Return is submitted to the state EPA portal by 30 September. Pollution incidents require immediate verbal notification and a written report within 7 days.',
    phases: AU_PHASES,
    parameters: AU_PARAMETERS,
    submissionDeadlines: [
      { event: 'Pollution incident verbal notification', timing: 'IMMEDIATELY — before attempting to control', authority: 'State EPA Environment Line / NRW' },
      { event: 'Pollution incident written report', timing: 'Within 7 days', authority: 'State EPA' },
      { event: 'Annual EPL Return submission', timing: '30 September each year', authority: 'State EPA online portal' },
      { event: 'EPL fee payment', timing: 'With annual return', authority: 'State EPA' },
      { event: 'Monitoring data publication (NSW)', timing: 'Within 30 days of results', authority: 'Company website (POEO Act s.66)' },
    ],
  },
  {
    code: 'EU_UWWTD',
    flag: '🇪🇺', name: 'EU Urban Wastewater Treatment Directive', shortName: 'EU UWWTD', country: 'European Union',
    accentClass: 'text-violet-400', borderClass: 'border-violet-500/30', bgClass: 'bg-violet-500/10',
    body: 'National Competent Authority (varies by member state) / European Environment Agency',
    legalBase: 'Urban Wastewater Treatment Directive 2024/3019 (recast) / Water Framework Directive 2000/60/EC / IED 2010/75/EU',
    permitType: 'Discharge Authorisation / Environmental Permit (member state)',
    certMark: 'National accreditation body (DAkkS / COFRAC / ENAC / ACCREDIA / UKAS)',
    retention: '5 years minimum (IED 2010/75/EU Article 72)',
    reportFreq: '12–24 samples/year; Annual national report to competent authority',
    samplesPerYear: '12 minimum (2,000–100,000 PE); 24 minimum (>100,000 PE)',
    complianceRate: '95-percentile of samples must meet concentration or removal % limits',
    overview: 'The EU Urban Wastewater Treatment Directive (recast 2024) requires member states to collect wastewater, treat it to secondary standard, and monitor effluent quality. 12 or 24 samples per year are required depending on plant size (PE). 95% must meet the limits in UWWTD Annex I Table 1. Annual data is reported to national competent authorities, who forward it to the European Environment Agency WISE database.',
    phases: EU_PHASES,
    parameters: EU_PARAMETERS,
    submissionDeadlines: [
      { event: 'Annual monitoring report to competent authority', timing: 'Q1 of following calendar year (member state specific)', authority: 'National competent authority' },
      { event: 'EEA WISE data submission', timing: 'Via member state — annual', authority: 'European Environment Agency' },
      { event: 'Permit exceedance notification', timing: 'Per national law — typically within 24–72 hours', authority: 'National competent authority' },
      { event: 'UWWTD compliance status review', timing: 'Annual — submitted by member state', authority: 'European Commission' },
    ],
  },
  {
    code: 'US_EPA',
    flag: '🇺🇸', name: 'US EPA / NPDES', shortName: 'US EPA', country: 'United States',
    accentClass: 'text-amber-400', borderClass: 'border-amber-500/30', bgClass: 'bg-amber-500/10',
    body: 'US Environmental Protection Agency (EPA) / State Delegated NPDES Authority',
    legalBase: 'Clean Water Act (CWA) §402 / 40 CFR Parts 122–125, 136 / State Implementation',
    permitType: 'NPDES Permit (individual or general permit)',
    certMark: 'EPA-certified / State-certified laboratory',
    retention: '3 years general; 5 years (DMR and permit records)',
    reportFreq: 'Monthly DMR; quarterly for some parameters; continuous for flow',
    samplesPerYear: '12+ monthly (CBOD/TSS); 52 weekly (pH typical); some daily',
    complianceRate: 'Zero-tolerance: any exceedance of monthly average or daily max is a CWA violation',
    overview: 'US EPA NPDES permits regulate point source discharges under the Clean Water Act. All monitoring must use EPA-approved methods from 40 CFR Part 136. Monthly Discharge Monitoring Reports (DMRs) are submitted electronically via the NPDES Electronic Reporting Tool (NeT) by the 28th day of the following month. Any permit exceedance triggers notification obligations. Technology-based limits (BPT/BAT/BCT) and water quality-based limits both apply.',
    phases: US_PHASES,
    parameters: US_PARAMETERS,
    submissionDeadlines: [
      { event: 'Monthly DMR submission', timing: '28th of month following monitoring month', authority: 'State NPDES authority / EPA NeT portal' },
      { event: 'Exceedance verbal notification', timing: 'Within 24 hours of discovery', authority: 'State NPDES director' },
      { event: 'Exceedance written report', timing: 'Within 5 days', authority: 'State NPDES authority' },
      { event: 'Annual NPDES permit re-certification (if required)', timing: 'Per permit schedule', authority: 'US EPA / state agency' },
      { event: 'NPDES permit renewal application', timing: '180 days before permit expiry', authority: 'State permitting authority' },
    ],
  },
];

/* ─── Step Card ─────────────────────────────────────────────────────────────── */
function StepCard({ step, index, expanded, onToggle, navigate }: {
  step: WorkflowStep;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  navigate: (path: string) => void;
}) {
  const Icon = step.icon;
  const criticalCount = step.tasks.filter(t => t.critical).length;
  return (
    <div className={`border rounded-xl transition-all duration-200 overflow-hidden
      ${expanded
        ? `${step.borderColor} bg-rb-surface/80 shadow-md`
        : `border-slate-800/40 bg-rb-surface/60 hover:border-slate-600/50 hover:bg-[#0d1e32]/70 cursor-pointer`
      }`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none" onClick={onToggle}>
        {/* Step number + icon */}
        <div className={`relative w-10 h-10 rounded-xl ${step.bgColor} border ${step.borderColor} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4.5 h-4.5 ${step.color}`} style={{ width: 18, height: 18 }} />
          <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-rb-surface border border-slate-700 flex items-center justify-center text-[9px] font-black text-slate-300">
            {index + 1}
          </span>
        </div>
        {/* Title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-white">{step.title}</span>
            {criticalCount > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/20 font-bold">
                {criticalCount} critical
              </span>
            )}
            {step.timing && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-800/50 flex items-center gap-0.5">
                <Clock className="w-2.5 h-2.5" />{step.timing}
              </span>
            )}
          </div>
          <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{step.subtitle}</p>
        </div>
        {/* Task count */}
        <span className="text-[10px] text-slate-500 shrink-0 hidden sm:block">{step.tasks.length} tasks</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${expanded ? `rotate-180 ${step.color}` : ''}`} />
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-800/40 px-4 pt-4 pb-5 space-y-4">
              {/* Regulatory refs */}
              <div className="flex flex-wrap gap-2">
                {step.regulatoryRef && (
                  <span className="text-[10px] px-2 py-1 rounded-lg bg-slate-800/60 text-slate-400 border border-slate-800/40 flex items-center gap-1">
                    <Shield className="w-3 h-3 text-slate-500" />{step.regulatoryRef}
                  </span>
                )}
                {step.methodStandard && (
                  <span className="text-[10px] px-2 py-1 rounded-lg bg-slate-800/60 text-slate-400 border border-slate-800/40 flex items-center gap-1">
                    <Beaker className="w-3 h-3 text-slate-500" />{step.methodStandard}
                  </span>
                )}
              </div>

              {/* Task checklist */}
              <div className="space-y-1.5">
                {step.tasks.map((task, ti) => (
                  <div key={ti} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg transition-colors
                    ${task.critical
                      ? 'bg-rose-500/5 border border-rose-500/15'
                      : 'bg-slate-800/30 border border-slate-800/30'
                    }`}>
                    <div className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded flex items-center justify-center
                      ${task.critical ? 'bg-rose-500/20 border border-rose-500/30' : 'bg-slate-700/60 border border-slate-600/40'}`}>
                      <CheckSquare className={`w-2.5 h-2.5 ${task.critical ? 'text-rose-400' : 'text-slate-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] leading-snug ${task.critical ? 'text-white font-medium' : 'text-slate-300'}`}>
                        {task.label}
                        {task.critical && <span className="ml-1.5 text-[9px] text-rose-400 font-bold uppercase tracking-wide">Required</span>}
                      </p>
                      {task.detail && <p className="text-[10px] text-slate-500 mt-0.5">{task.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action link */}
              {step.link && (
                <button
                  onClick={() => navigate(step.link!.path)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all
                    ${step.bgColor} ${step.color} ${step.borderColor} hover:opacity-80`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {step.link.label}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function SampleWorkflowPage() {
  const navigate = useNavigate();
  const { activeStandard, currentCustomer } = useSession();

  // Default to current session standard or UK_EA
  const defaultCode = ((currentCustomer as any)?.standard || activeStandard || 'UK_EA') as StandardCode;
  const [selectedCode, setSelectedCode] = useState<StandardCode>(defaultCode);
  const [expandedStep, setExpandedStep]   = useState<string | null>('phase_0');
  const [activeSection, setActiveSection] = useState<'workflow' | 'parameters' | 'deadlines'>('workflow');

  const std = STANDARDS.find(s => s.code === selectedCode) ?? STANDARDS[0];
  const toggleStep = (id: string) => setExpandedStep(prev => prev === id ? null : id);

  return (
    <div className="space-y-5 pb-8">

      <PageHeader
        icon={BookOpen}
        title="Sample Workflow Guide"
        subtitle="Step-by-step guide for regulatory sample collection and submission"
        actions={
          <button
            onClick={() => navigate('/influent-entry')}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all"
          >
            <Droplets className="w-3.5 h-3.5" /> Start New Sample
          </button>
        }
      />

      {/* ── Standard selector ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {STANDARDS.map(s => (
          <button
            key={s.code}
            onClick={() => { setSelectedCode(s.code); setExpandedStep('phase_0'); }}
            className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl border text-left transition-all
              ${selectedCode === s.code
                ? `${s.bgClass} ${s.borderClass} shadow-sm`
                : 'border-slate-800/40 bg-rb-surface/60 hover:border-slate-600/50 hover:bg-slate-800/30'
              }`}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-base leading-none">{s.flag}</span>
              <span className={`text-[10px] font-black uppercase tracking-wide ${selectedCode === s.code ? s.accentClass : 'text-slate-400'}`}>
                {s.shortName}
              </span>
            </div>
            <span className="text-[9px] text-slate-500 leading-tight">{s.country}</span>
          </button>
        ))}
      </div>

      {/* ── Standard overview banner ── */}
      <div className={`rounded-xl border ${std.borderClass} ${std.bgClass} p-4 space-y-3`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{std.flag}</span>
              <span className={`text-sm font-black ${std.accentClass}`}>{std.name}</span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold border ${std.borderClass} ${std.accentClass}`}>
                {std.certMark}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed max-w-2xl">{std.overview}</p>
          </div>
        </div>

        {/* Key metrics strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: 'Regulatory Body', value: std.body, icon: Building2 },
            { label: 'Permit Type', value: std.permitType, icon: Hash },
            { label: 'Monitoring Frequency', value: std.reportFreq, icon: Calendar },
            { label: 'Record Retention', value: std.retention, icon: Archive },
          ].map(m => {
            const MIcon = m.icon;
            return (
              <div key={m.label} className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                <div className="flex items-center gap-1 mb-1">
                  <MIcon className="w-3 h-3 text-slate-500" />
                  <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{m.label}</p>
                </div>
                <p className="text-[10px] text-slate-200 leading-snug font-medium">{m.value}</p>
              </div>
            );
          })}
        </div>

        {/* Legal basis */}
        <div className="flex items-start gap-2 bg-slate-800/30 border border-slate-800/40 rounded-lg px-3 py-2">
          <Shield className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-slate-400 leading-snug"><span className="text-slate-300 font-semibold">Legal Basis: </span>{std.legalBase}</p>
        </div>
      </div>

      {/* ── Section tabs ── */}
      <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-800/50 rounded-xl p-1 w-fit">
        {([
          { id: 'workflow',   label: 'Step-by-Step Workflow', icon: ClipboardList },
          { id: 'parameters', label: 'Required Parameters',   icon: FlaskConical },
          { id: 'deadlines',  label: 'Submission Deadlines',  icon: Calendar },
        ] as const).map(tab => {
          const TIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                activeSection === tab.id
                  ? 'bg-rb-brand text-rb-surface font-bold'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <TIcon className="w-3.5 h-3.5" />{tab.label}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════
          SECTION: WORKFLOW STEPS
      ══════════════════════════════════════════════ */}
      {activeSection === 'workflow' && (
        <div className="space-y-3">
          {/* Expand/collapse all */}
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-slate-500 flex items-center gap-1.5">
              <Info className="w-3 h-3" />
              Click any phase to expand required steps. <span className="text-rose-400">Red items</span> are mandatory regulatory requirements.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setExpandedStep(null)}
                className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors">Collapse all</button>
              <span className="text-slate-400">·</span>
              <button onClick={() => setExpandedStep('all')}
                className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors">Expand all</button>
            </div>
          </div>

          {/* Connector + step cards */}
          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-[1.18rem] top-10 bottom-10 w-0.5 bg-gradient-to-b from-slate-700/60 via-slate-700/40 to-transparent z-0 hidden sm:block" />

            <div className="space-y-2 relative z-10">
              {std.phases.map((step, i) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={i}
                  expanded={expandedStep === `phase_${i}` || expandedStep === 'all'}
                  onToggle={() => {
                    if (expandedStep === 'all') {
                      setExpandedStep(null);
                    } else {
                      toggleStep(`phase_${i}`);
                    }
                  }}
                  navigate={navigate}
                />
              ))}
            </div>
          </div>

          {/* Quick links footer */}
          <div className="rb-card border border-slate-800/40 p-4 mt-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-3">Quick Navigation</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { label: 'Influent Entry', path: '/influent-entry', icon: Droplets },
                { label: 'Influent Records', path: '/influent', icon: Layers },
                { label: 'Effluent Entry', path: '/effluent-entry', icon: Beaker },
                { label: 'Effluent Records', path: '/effluent', icon: Layers },
                { label: 'Compliance Gateway', path: '/compliance-gateway', icon: Shield },
                { label: 'Generate Report', path: '/samples', icon: FileText },
              ].map(l => {
                const LIcon = l.icon;
                return (
                  <button
                    key={l.path}
                    onClick={() => navigate(l.path)}
                    className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg bg-slate-800/40 border border-slate-800/40 hover:border-rb-brand/30 hover:bg-rb-brand/5 transition-all group"
                  >
                    <LIcon className="w-4 h-4 text-slate-500 group-hover:text-rb-brand transition-colors" />
                    <span className="text-[9px] text-slate-400 group-hover:text-slate-200 text-center leading-tight transition-colors">{l.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          SECTION: REQUIRED PARAMETERS
      ══════════════════════════════════════════════ */}
      {activeSection === 'parameters' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-rb-brand" />
            <h3 className="text-sm font-bold text-white">{std.name} — Required Monitoring Parameters</h3>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-800/40">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-800/80 border-b border-slate-800/40">
                  <th className="text-left px-4 py-3 text-slate-300 font-bold">Parameter</th>
                  <th className="text-left px-3 py-3 text-slate-300 font-bold">Unit</th>
                  <th className="text-left px-3 py-3 text-slate-300 font-bold">Discharge Limit</th>
                  <th className="text-left px-3 py-3 text-slate-300 font-bold">Monitoring Frequency</th>
                  <th className="text-left px-3 py-3 text-slate-300 font-bold">Approved Method</th>
                </tr>
              </thead>
              <tbody>
                {std.parameters.map((p, i) => (
                  <tr key={p.param} className={`border-b border-slate-800/50 transition-colors hover:bg-slate-800/20
                    ${p.critical ? 'bg-rose-500/3' : i % 2 === 0 ? 'bg-slate-800/15' : 'bg-transparent'}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {p.critical && <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0" />}
                        <span className={`font-bold ${p.critical ? 'text-white' : 'text-slate-200'}`}>{p.param}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400 font-mono">{p.unit}</td>
                    <td className="px-3 py-2.5">
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded
                        ${p.critical ? 'bg-rose-500/10 text-rose-300 border border-rose-500/20' : 'bg-slate-800 text-slate-300'}`}>
                        {p.limit}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">{p.frequency}</td>
                    <td className="px-3 py-2.5 text-slate-500 font-mono text-[10px]">{p.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-start gap-2 bg-slate-800/40 border border-slate-800/40 rounded-xl px-4 py-3">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-[10px] text-slate-400 space-y-0.5">
              <p><span className="text-white font-semibold">Note:</span> Limits shown are typical for the standard. Actual permit / licence conditions are site-specific and may be more or less stringent.</p>
              <p>Monitoring programme may include additional parameters based on industry sector, receiving water sensitivity, and permit conditions. Always defer to the issued permit.</p>
              <p className={`font-semibold ${std.accentClass}`}>Accreditation required: {std.certMark}</p>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          SECTION: SUBMISSION DEADLINES
      ══════════════════════════════════════════════ */}
      {activeSection === 'deadlines' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-rb-brand" />
            <h3 className="text-sm font-bold text-white">{std.name} — Submission Deadlines & Notifications</h3>
          </div>

          <div className="space-y-2">
            {std.submissionDeadlines.map((d, i) => (
              <div key={i} className={`flex items-start gap-4 px-4 py-3 rounded-xl border
                ${d.timing.toLowerCase().includes('immediately') || d.timing.toLowerCase().includes('24 hour')
                  ? 'border-rose-500/25 bg-rose-500/5'
                  : 'border-slate-800/40 bg-slate-800/20'
                }`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  ${d.timing.toLowerCase().includes('immediately') || d.timing.toLowerCase().includes('24 hour')
                    ? 'bg-rose-500/15 border border-rose-500/25'
                    : 'bg-slate-700/60 border border-slate-600/40'
                  }`}>
                  {d.timing.toLowerCase().includes('immediately')
                    ? <AlertTriangle className="w-4 h-4 text-rose-400" />
                    : <Calendar className="w-4 h-4 text-slate-400" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold leading-snug ${
                    d.timing.toLowerCase().includes('immediately') ? 'text-rose-300' : 'text-white'
                  }`}>{d.event}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className={`text-[10px] flex items-center gap-1 font-semibold
                      ${d.timing.toLowerCase().includes('immediately') || d.timing.toLowerCase().includes('24 hour')
                        ? 'text-rose-400' : 'text-amber-400'}`}>
                      <Clock className="w-3 h-3" />{d.timing}
                    </span>
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />{d.authority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Retention note */}
          <div className={`rounded-xl border ${std.borderClass} ${std.bgClass} px-4 py-3`}>
            <p className="text-[10px] font-bold text-white mb-1">Record Retention Requirement</p>
            <p className={`text-sm font-black ${std.accentClass}`}>{std.retention}</p>
            <p className="text-[10px] text-slate-400 mt-1">
              All monitoring data, laboratory reports, chain-of-custody records, field sheets, calibration records, and regulatory correspondence must be retained and produced on request by the regulatory authority.
            </p>
          </div>

          <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-300">
              <span className="font-bold">Regulatory Disclaimer:</span> Deadlines and requirements shown are based on the standard regulatory framework and may vary based on individual permit conditions, member state implementation, or specific licence schedules. Always refer to your issued permit and seek legal/regulatory advice for site-specific compliance obligations.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
